'use client';

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronRight } from 'lucide-react';
import { usePortalContainer } from '@church/nextjs-ui/bottom-sheet';

/**
 * A Collection's sign-up, on a phone: the event page's register bar
 * (event-details RegisterDock), copied for the Who card.
 *
 * On a phone the Who card stacks under the description and the checklist, so
 * the bar brings it to the reader: the ways in on the left ("Volunteer ·
 * Receive a box"), the card's own button on the right, opening the same sheet.
 *
 * IT GETS OUT OF THE WAY when the Who card is on screen (the card says the
 * same thing and the bar would cover it) and while the sheet is open, and it
 * leaves the way it came, sliding down rather than blinking out. It stays
 * mounted while away, `inert` so a bar nobody can see never takes a tap.
 *
 * The slide is on the outer box and the first rise on the card inside, for the
 * reason the event bar gives: an animation holding `both` would outrank the
 * transition and the bar could never leave.
 *
 * Portalled to the widget's overlay host: the theme's stacking contexts break
 * `position: fixed` rendered in place.
 */
export function CollectionDock({
  label,
  ways,
  anchor,
  hidden = false,
  accentStyle,
  onOpen,
}: {
  /** The Who card's own words, so the two buttons never disagree. */
  label: string;
  /** "Volunteer", "Receive a box" */
  ways: string[];
  /** The Who card. While it is on screen the bar stands down. */
  anchor: React.RefObject<HTMLElement | null>;
  /** The sheet is open. */
  hidden?: boolean;
  accentStyle?: React.CSSProperties;
  onOpen: () => void;
}) {
  const host = usePortalContainer();
  const [cardSeen, setCardSeen] = useState(false);

  useEffect(() => {
    const el = anchor.current;
    // No card to watch, or no observer: the bar stays, the safe way to be wrong.
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry) setCardSeen(entry.isIntersecting);
      },
      // The bar covers the bottom of the window, so the card only counts once
      // it is properly on screen, not sliding under the bar.
      { threshold: 0.15, rootMargin: '0px 0px -96px 0px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [anchor]);

  const away = hidden || cardSeen;

  const bar = (
    <div
      className="pointer-events-none fixed inset-x-0 bottom-0 z-[60] flex flex-col px-3 lg:hidden"
      style={{ ...accentStyle, paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}
    >
      <div
        inert={away}
        aria-hidden={away}
        /* `translate`, not `transform`: Tailwind v4 moves things with the
           `translate` property, so a transition on `transform` animates nothing. */
        className={`mx-auto w-full max-w-[28rem] min-w-0 transition-[translate,opacity] duration-300 ease-out motion-reduce:transition-none ${
          away
            ? 'pointer-events-none translate-y-[calc(100%+2rem)] opacity-0'
            : 'pointer-events-auto translate-y-0 opacity-100'
        }`}
      >
        <button
          type="button"
          onClick={onOpen}
          className="dock-rise flex w-full min-w-0 items-center gap-3 rounded-2xl border border-[var(--color-line,#e5e7eb)] bg-white/95 px-4 py-3 text-left shadow-[var(--shadow-lift)] backdrop-blur"
        >
          <span className="[display:-webkit-box] min-w-0 flex-1 [overflow:hidden] text-sm leading-snug font-extrabold text-[#1c2b39] [-webkit-box-orient:vertical] [-webkit-line-clamp:2]">
            {ways.join(' · ')}
          </span>
          <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-[var(--accent,#299c8f)] px-5 py-3 text-base font-bold text-[var(--on-accent,#fff)] shadow-sm max-[380px]:px-4">
            {label}
            <ChevronRight className="h-4 w-4" strokeWidth={2.5} />
          </span>
        </button>
      </div>
    </div>
  );

  return host ? createPortal(bar, host) : bar;
}
